import ObjectScreen from '@components/v2/layout/ObjectScreen/ObjectScreen';
import Button from '@components/v2/ui/Button/Button';
import Icon from '@components/v2/ui/Icon/Icon';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import Tabs from '@components/v2/ui/Tabs/Tabs';
import Typography from '@components/v2/ui/Typography/Typography';
import { Stack } from '@mui/material';
import { Pencil, Plus } from 'lucide-react';
import LangEnum from '../../../../../../../../lang/LangEnum';
import { CatalogProductsTestIds } from '../../../../../../../../utils/testIds/CatalogProducts.testIds';
import { useTranslation } from '../../../../../../../translation/TranslationContext';
import { CatalogCreationAccessCatalogDates } from '../../../../../../creation/catalogCreationAccess.types';
import CatalogAccessEditModal from './components/CatalogAccessEditModal';
import { useCatalogAccessSection } from './hooks/useCatalogAccessSection';

interface CatalogAccessSectionProps {
  catalogId: string;
  isReadOnly: boolean;
  catalogDates: CatalogCreationAccessCatalogDates;
}

const CatalogAccessSection = ({
  catalogId,
  isReadOnly,
  catalogDates,
}: CatalogAccessSectionProps) => {
  const { translate } = useTranslation();
  const {
    tabs,
    countByField,
    selectedField,
    entityNames,
    emptyLabel,
    isLoading,
    hasLoadError,
    isEditModalOpen,
    handleSelectTab,
    openEditModal,
    closeEditModal,
  } = useCatalogAccessSection({ catalogId });

  const translatedTabs = tabs.map((tab) => ({ ...tab, label: translate(tab.label) }));

  return (
    <>
      <ObjectScreen.Section
        title={LangEnum.CATALOGS_CREATION_MODAL_SECTION_ACCESS}
        isDefaultOpen
        persistKey="catalog-details:access"
        testId={CatalogProductsTestIds.DETAILS_ACCESS_SECTION}
        action={
          !isReadOnly && (
            <IconButton
              icon={Pencil}
              size="medium"
              ariaLabel={translate(LangEnum.GLOBAL_EDIT)}
              testId={CatalogProductsTestIds.DETAILS_ACCESS_EDIT}
              onClick={openEditModal}
            />
          )
        }
      >
        <Tabs items={translatedTabs} selectedId={selectedField} onSelect={handleSelectTab} />

        {!isLoading && (
          <Stack sx={{ gap: 1.5, pt: 0.75 }}>
            {emptyLabel ? (
              <Typography
                variant="body2"
                sx={{ color: hasLoadError ? 'error.main' : 'text.secondary' }}
              >
                {translate(emptyLabel)}
              </Typography>
            ) : (
              entityNames.map((entityName) => (
                <Typography key={entityName} variant="body2" noWrap>
                  {entityName}
                </Typography>
              ))
            )}

            <Button
              variant="text"
              color="secondary"
              size="small"
              sx={{ alignSelf: 'flex-start' }}
              testId={CatalogProductsTestIds.DETAILS_ACCESS_SHOW_MORE}
              startIcon={<Icon icon={Plus} size="sm" />}
              onClick={openEditModal}
            >
              {translate(LangEnum.GLOBAL_MORE_DETAILS)}
            </Button>
          </Stack>
        )}
      </ObjectScreen.Section>

      {isEditModalOpen && (
        <CatalogAccessEditModal
          catalogId={catalogId}
          initialField={selectedField}
          countByField={countByField}
          catalogDates={catalogDates}
          isReadOnly={isReadOnly}
          onClose={closeEditModal}
        />
      )}
    </>
  );
};

export default CatalogAccessSection;
